/**
 * Section study sub-components for CardingWasteStudiesPage.
 * Sprint 0142 — splits the matrix view out of the parent page for
 * readability. Imported by CardingWasteStudiesPage.tsx.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Badge, Button, Card, Col, DatePicker, Divider, Drawer, Form,
  Input, InputNumber, Modal, Row, Select, Space, Table, Tag, Typography, message,
} from 'antd'
import {
  CheckCircleOutlined, ExperimentOutlined, FileExcelOutlined,
  PlusOutlined, SignatureOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'

import {
  cardingApi, type CardWasteSectionStudy, type CardWasteStudy,
} from '@/features/production/cardingApi'

const { Text, Paragraph } = Typography

const STATUS_COLOR: Record<string, string> = {
  DRAFT: 'default',
  COMPLETED: 'blue',
  SIGNED: 'green',
}

interface MachineLite { id: string; machine_code: string; machine_name: string }

// ─── Single-card history tab (shows only ad-hoc studies) ──────────────

export function SingleCardTab({
  rows, columns, loading, machineOptions, machineFilter, setMachineFilter, onNew,
}: {
  rows: CardWasteStudy[]
  columns: any[]
  loading: boolean
  machineOptions: { label: string; value: string }[]
  machineFilter: string | undefined
  setMachineFilter: (v: string | undefined) => void
  onNew: () => void
}) {
  return (
    <>
      <Alert
        type="info"
        showIcon
        icon={<ExperimentOutlined />}
        style={{ marginBottom: 16 }}
        message="Ad-hoc single-card SOP"
        description={
          <span>
            Use this for one-off studies — new card commissioning, after
            cylinder/flat change, post-breakdown verification. Monthly
            routine should be a Section Study instead.
          </span>
        }
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Text strong>Card:</Text>
          <Select
            allowClear
            style={{ width: 240 }}
            placeholder="All cards"
            value={machineFilter}
            onChange={setMachineFilter}
            options={machineOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            }
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={onNew}>
            New Single-card Study
          </Button>
        </Space>
      </Card>
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        dataSource={rows.filter((r) => !r.section_study_id)}
        columns={columns}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 50 }}
      />
    </>
  )
}

// ─── Section Studies tab — list + create + matrix open ──────────────

export function SectionStudiesTab({
  plantId, machineMap, machineOptions, countOptions,
}: {
  plantId: string
  machineMap: Map<string, MachineLite>
  machineOptions: { label: string; value: string }[]
  countOptions: { label: string; value: string }[]
}) {
  const qc = useQueryClient()
  const [createOpen, setCreateOpen] = useState(false)
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null)
  const [createForm] = Form.useForm()

  const sectionsQ = useQuery({
    enabled: !!plantId,
    queryKey: ['section-studies', plantId],
    queryFn: () => cardingApi.listSections({ plant_id: plantId, limit: 50 }),
  })

  const createMut = useMutation({
    mutationFn: cardingApi.createSection,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['section-studies'] })
      setCreateOpen(false)
      createForm.resetFields()
      setActiveSectionId(data.section.id)
      message.success(`Section study created with ${data.children.length} cards`)
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Create failed'),
  })

  const sections = sectionsQ.data ?? []

  return (
    <>
      <Alert
        type="info"
        showIcon
        icon={<ExperimentOutlined />}
        style={{ marginBottom: 16 }}
        message="Section Study SOP — measure all cards in parallel"
        description={
          <span>
            <Text strong>One day, all cards.</Text> Set ambient temp + RH once,
            sample duration once (60 min default). Open all waste bins
            simultaneously. Run all cards untouched for the window. Weigh
            each card&apos;s 6 components + sliver output. Matrix view computes
            per-card waste %, totals, and exports to Excel matching the
            CARD WISE SECTION WASTE STUDY format. QM signs the whole
            section in one click; each card&apos;s standard updates at once.
          </span>
        }
      />

      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              createForm.resetFields()
              createForm.setFieldsValue({
                study_date: dayjs(),
                sample_duration_min: 60,
              })
              setCreateOpen(true)
            }}
          >
            New Section Study
          </Button>
          <Badge
            count={sections.filter((s) => s.status !== 'SIGNED').length}
            style={{ backgroundColor: '#faad14' }}
            offset={[10, 0]}
          >
            <Text type="secondary">unsigned</Text>
          </Badge>
        </Space>
      </Card>

      <Table
        size="small"
        rowKey="id"
        loading={sectionsQ.isLoading}
        dataSource={sections}
        columns={[
          {
            title: 'Date', dataIndex: 'study_date', width: 110,
            render: (v: string) => dayjs(v).format('DD MMM YYYY'),
          },
          {
            title: 'Status', dataIndex: 'status', width: 110,
            render: (v: string) => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>,
          },
          {
            title: 'Duration', dataIndex: 'sample_duration_min', width: 90,
            render: (v: number) => `${v} min`,
          },
          {
            title: 'Ambient', key: 'ambient', width: 140,
            render: (_: unknown, r: CardWasteSectionStudy) =>
              r.ambient_temp_c && r.ambient_rh_pct
                ? `${r.ambient_temp_c}°C / ${r.ambient_rh_pct}% RH` : '—',
          },
          {
            title: 'Signed', dataIndex: 'qm_signed_at', width: 140,
            render: (v: string | null) => v
              ? <Tag color="green">{dayjs(v).format('DD MMM HH:mm')}</Tag>
              : <Tag>Pending</Tag>,
          },
          {
            title: '', key: 'action', fixed: 'right' as const, width: 90,
            render: (_: unknown, r: CardWasteSectionStudy) =>
              <Button size="small" onClick={() => setActiveSectionId(r.id)}>Open</Button>,
          },
        ]}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 30 }}
      />

      <Drawer
        title="New Section Study — Setup"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        width={520}
        destroyOnClose
      >
        <Paragraph type="secondary">
          One child row per card auto-created. Default scope = all active
          Carding machines in the plant. Conditions apply to every card
          (shared ambient + duration). Untick cards not running today.
        </Paragraph>
        <Form
          form={createForm}
          layout="vertical"
          onFinish={(v) => {
            if (!plantId) return message.error('Plant not loaded')
            createMut.mutate({
              plant_id: plantId,
              study_date: (v.study_date as dayjs.Dayjs).format('YYYY-MM-DD'),
              count_id: v.count_id,
              ambient_temp_c: v.ambient_temp_c,
              ambient_rh_pct: v.ambient_rh_pct,
              sample_duration_min: v.sample_duration_min ?? 60,
              notes: v.notes,
              card_machine_ids: v.card_machine_ids,
            })
          }}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="study_date" label="Study date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sample_duration_min" label="Sample duration (min)">
                <InputNumber min={15} max={240} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="count_id" label="Count being run (optional)">
            <Select allowClear options={countOptions} placeholder="(any)" />
          </Form.Item>
          <Divider orientation="left">Ambient conditions</Divider>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="ambient_temp_c" label="Temp (°C)">
                <InputNumber min={0} max={60} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="ambient_rh_pct" label="Relative humidity (%)">
                <InputNumber min={0} max={100} step={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="card_machine_ids"
            label="Cards to include"
            tooltip="Leave empty to auto-include all active CARDING machines (recommended). Pick a subset only if some cards are not running today."
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="(all active cards)"
              options={machineOptions}
              filterOption={(input, option) =>
                (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          <Form.Item name="notes" label="Notes (optional)">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={createMut.isPending} block>
              Create section study
            </Button>
          </Form.Item>
        </Form>
      </Drawer>

      {activeSectionId && (
        <SectionMatrixDrawer
          sectionId={activeSectionId}
          machineMap={machineMap}
          onClose={() => setActiveSectionId(null)}
        />
      )}
    </>
  )
}

// ─── Section matrix view — cards as columns, components as rows ────

function SectionMatrixDrawer({
  sectionId, machineMap, onClose,
}: {
  sectionId: string
  machineMap: Map<string, MachineLite>
  onClose: () => void
}) {
  const qc = useQueryClient()
  const sectionQ = useQuery({
    queryKey: ['section-study', sectionId],
    queryFn: () => cardingApi.getSection(sectionId),
  })

  const [edits, setEdits] = useState<Record<string, Record<string, number | undefined>>>({})

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) =>
      cardingApi.patchStudy(id, body),
  })
  const completeMut = useMutation({
    mutationFn: () => cardingApi.completeSection(sectionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['section-study', sectionId] })
      qc.invalidateQueries({ queryKey: ['section-studies'] })
      message.success('Section completed — review then sign')
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Complete failed'),
  })
  const signMut = useMutation({
    mutationFn: () => cardingApi.signSection(sectionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['section-study', sectionId] })
      qc.invalidateQueries({ queryKey: ['section-studies'] })
      qc.invalidateQueries({ queryKey: ['carding-settings'] })
      message.success('Signed — each card standard updated for daily prediction')
    },
    onError: (e: any) => message.error(e?.response?.data?.detail ?? 'Sign failed'),
  })

  if (!sectionQ.data) {
    return (
      <Drawer open onClose={onClose} width="95%" title="Loading…">
        <div style={{ padding: 32, textAlign: 'center' }}>Loading section…</div>
      </Drawer>
    )
  }
  const { section, children } = sectionQ.data
  const editable = section.status === 'DRAFT'

  const flushEdits = async () => {
    const dirty = Object.entries(edits).filter(([, b]) => Object.keys(b).length > 0)
    if (dirty.length === 0) return
    await Promise.all(
      dirty.map(([id, body]) => patchMut.mutateAsync({ id, body }))
    )
    setEdits({})
    qc.invalidateQueries({ queryKey: ['section-study', sectionId] })
  }

  const handleCellEdit = (childId: string, field: string, v: number | null) => {
    setEdits((prev) => ({
      ...prev,
      [childId]: { ...(prev[childId] ?? {}), [field]: v ?? undefined },
    }))
  }

  const getCellValue = (child: CardWasteStudy, field: keyof CardWasteStudy): number | undefined => {
    const buf = edits[child.id]
    if (buf && field in buf) return buf[field as string]
    const v = child[field]
    return typeof v === 'number' ? v : undefined
  }

  const COMPONENT_ROWS: Array<{ label: string; field: keyof CardWasteStudy }> = [
    { label: 'LIQREIN WASTE KG',          field: 'lickerin_kg' },
    { label: 'FLAT TOP WASTE KG',         field: 'flat_strips_kg' },
    { label: 'SFL WASTE KG',              field: 'sfl_kg' },
    { label: 'SFD WASTE KG',              field: 'sfd_kg' },
    { label: 'DOFFER WASTE KG',           field: 'doffer_kg' },
    { label: 'UNDER FEED ROLL WASTE KG',  field: 'under_feed_roll_kg' },
  ]

  const totalWasteKg = (child: CardWasteStudy): number => {
    return COMPONENT_ROWS.reduce((s, r) => s + (getCellValue(child, r.field) ?? 0), 0)
  }
  const totalInputKg = (child: CardWasteStudy): number => {
    return (getCellValue(child, 'sample_output_kg') ?? 0) + totalWasteKg(child)
  }
  const wastePct = (child: CardWasteStudy): number | null => {
    const input = totalInputKg(child)
    return input > 0 ? (totalWasteKg(child) / input) * 100 : null
  }

  return (
    <Drawer
      open
      onClose={onClose}
      width="95%"
      title={
        <Space>
          <Text strong>CARD WISE SECTION WASTE STUDY</Text>
          <Tag color={STATUS_COLOR[section.status]}>{section.status}</Tag>
          <Text type="secondary">
            {dayjs(section.study_date).format('DD MMM YYYY')} ·
            {' '}{section.sample_duration_min} min ·
            {section.ambient_temp_c ? ` ${section.ambient_temp_c}°C` : ''}
            {section.ambient_rh_pct ? ` / ${section.ambient_rh_pct}% RH` : ''}
            {' '}· {children.length} cards
          </Text>
        </Space>
      }
      extra={
        <Space>
          {editable && (
            <>
              <Button onClick={flushEdits} loading={patchMut.isPending}>
                Save edits
              </Button>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={completeMut.isPending}
                onClick={() => {
                  Modal.confirm({
                    title: 'Complete this section study?',
                    content: 'Each card with measurements gets locked + waste % computed. Cards with no measurements stay DRAFT.',
                    onOk: async () => {
                      await flushEdits()
                      completeMut.mutate()
                    },
                  })
                }}
              >
                Complete All
              </Button>
            </>
          )}
          {section.status === 'COMPLETED' && (
            <Button
              type="primary"
              icon={<SignatureOutlined />}
              loading={signMut.isPending}
              onClick={() => {
                Modal.confirm({
                  title: 'QM Sign-off entire section?',
                  content: 'Signs all COMPLETED children. Each card standard waste % updates to its measured value. Daily Carding section prediction will use these going forward. Immutable.',
                  onOk: () => signMut.mutate(),
                })
              }}
            >
              QM Sign All
            </Button>
          )}
          <Button
            icon={<FileExcelOutlined />}
            onClick={async () => {
              try {
                const res = await cardingApi.exportSectionXlsx(sectionId)
                const blob = res.data instanceof Blob
                  ? res.data
                  : new Blob([res.data as BlobPart])
                const url = window.URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `card_waste_section_${dayjs(section.study_date).format('YYYY-MM-DD')}.xlsx`
                document.body.appendChild(a)
                a.click()
                a.remove()
                window.URL.revokeObjectURL(url)
              } catch {
                message.error('Excel export failed')
              }
            }}
          >
            Export to Excel
          </Button>
        </Space>
      }
      destroyOnClose
    >
      <Alert
        type={editable ? 'warning' : section.status === 'SIGNED' ? 'success' : 'info'}
        showIcon
        style={{ marginBottom: 12 }}
        message={
          editable
            ? 'Edit cells in place. Click "Save edits" to flush, then "Complete All" to lock + compute. Operator may leave some cards blank (they stay DRAFT).'
            : section.status === 'COMPLETED'
              ? 'All cards locked + waste % computed. Review the bottom row, then QM signs to apply.'
              : 'Signed — immutable. Each card standard reflects its measured value. Export the printed copy for the QM file.'
        }
      />

      <div style={{ overflowX: 'auto' }}>
        <table style={{
          borderCollapse: 'collapse',
          width: '100%',
          fontSize: 13,
        }}>
          <thead>
            <tr>
              <th style={cellStyle({ header: true, label: true })}>CARD NO</th>
              {children.map((c) => (
                <th key={c.id} style={cellStyle({ header: true })}>
                  {machineMap.get(c.card_machine_id)?.machine_code ?? c.card_machine_id.slice(0, 6)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={cellStyle({ label: true })}>PROD.</td>
              {children.map((c) => (
                <td key={c.id} style={cellStyle()}>
                  {editable ? (
                    <InputNumber
                      size="small"
                      min={0}
                      step={0.01}
                      value={getCellValue(c, 'sample_output_kg')}
                      onChange={(v) => handleCellEdit(c.id, 'sample_output_kg', v)}
                      style={{ width: '100%' }}
                      controls={false}
                    />
                  ) : (c.sample_output_kg?.toFixed(2) ?? '—')}
                </td>
              ))}
            </tr>
            <tr style={{ background: '#fafafa' }}>
              <td style={cellStyle({ label: true })}>PROD.+ WASTE</td>
              {children.map((c) => (
                <td key={c.id} style={cellStyle({ computed: true })}>
                  {totalInputKg(c) > 0
                    ? totalInputKg(c).toFixed(2)
                    : (c.sample_input_kg?.toFixed(2) ?? '—')}
                </td>
              ))}
            </tr>
            <tr><td colSpan={children.length + 1} style={{ height: 6 }} /></tr>

            {COMPONENT_ROWS.map((row) => (
              <tr key={row.field}>
                <td style={cellStyle({ label: true })}>{row.label}</td>
                {children.map((c) => (
                  <td key={c.id} style={cellStyle()}>
                    {editable ? (
                      <InputNumber
                        size="small"
                        min={0}
                        step={0.001}
                        value={getCellValue(c, row.field)}
                        onChange={(v) => handleCellEdit(c.id, row.field as string, v)}
                        style={{ width: '100%' }}
                        controls={false}
                      />
                    ) : (
                      (c[row.field] as number | null)?.toFixed(3) ?? '—'
                    )}
                  </td>
                ))}
              </tr>
            ))}

            <tr style={{ background: '#e6f4ff', fontWeight: 600 }}>
              <td style={cellStyle({ label: true })}>TOTAL WASTE KG</td>
              {children.map((c) => (
                <td key={c.id} style={cellStyle({ computed: true })}>
                  {totalWasteKg(c) > 0
                    ? totalWasteKg(c).toFixed(3)
                    : (c.sample_waste_kg?.toFixed(3) ?? '—')}
                </td>
              ))}
            </tr>

            <tr><td colSpan={children.length + 1} style={{ height: 6 }} /></tr>

            {COMPONENT_ROWS.map((row) => (
              <tr key={`${row.field}-pct`}>
                <td style={cellStyle({ label: true })}>{row.label.replace('KG', '%')}</td>
                {children.map((c) => {
                  const num = getCellValue(c, row.field) ?? 0
                  const denom = totalInputKg(c)
                  const pct = denom > 0 ? (num / denom) * 100 : null
                  return (
                    <td key={c.id} style={cellStyle({ computed: true })}>
                      {pct !== null ? pct.toFixed(2) : '—'}
                    </td>
                  )
                })}
              </tr>
            ))}

            <tr style={{ background: '#fff7e6', fontWeight: 600 }}>
              <td style={cellStyle({ label: true })}>TOTAL WASTE %</td>
              {children.map((c) => {
                const pct = wastePct(c)
                const color = pct === null
                  ? undefined
                  : pct > 6 ? '#ff4d4f' : pct > 5 ? '#faad14' : '#52c41a'
                return (
                  <td key={c.id} style={{ ...cellStyle({ computed: true }), color }}>
                    {pct !== null ? `${pct.toFixed(2)}%` : (c.waste_pct?.toFixed(2) ?? '—')}
                  </td>
                )
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {section.notes && (
        <Card size="small" style={{ marginTop: 12 }} title="Notes">
          <Text>{section.notes}</Text>
        </Card>
      )}
    </Drawer>
  )
}

function cellStyle(opts: { header?: boolean; label?: boolean; computed?: boolean } = {}): React.CSSProperties {
  return {
    border: '1px solid #d9d9d9',
    padding: opts.header || opts.label ? '6px 10px' : '2px 4px',
    textAlign: opts.label ? 'left' : 'right',
    fontWeight: opts.header || opts.label ? 600 : opts.computed ? 500 : 400,
    background: opts.header ? '#fafafa' : undefined,
    minWidth: 70,
    whiteSpace: 'nowrap',
  }
}
